import { styled } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import SelectContent from './SelectContent'
import MenuContent from './MenuContent'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import IconButton from '@mui/material/IconButton'
import { useAccessToken } from '@services/token/token.hooks'
import { useAtomValue } from 'jotai'
import { userAtom } from '@services/token/token.models'
import { useMemo, useState, useEffect } from 'react'
import Calendar from '@components/Calendar'
import NetworkState from '@components/NetworkState'
import OfflineStatus from '@components/OfflineStatus'

const drawerWidth = 340

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box'
  }
})

export default function SideMenu() {
  const { clearUserData } = useAccessToken()
  const user = useAtomValue(userAtom)

  // Sprawdź czy to logowanie przez Microsoft OAuth
  const [userData, setUserData] = useState<any>(null)

  useEffect(() => {
    const msUserData = localStorage.getItem('user_data')
    if (msUserData && msUserData !== 'undefined' && msUserData !== '{}') {
      try {
        const parsed = JSON.parse(msUserData)
        if (Object.keys(parsed).length > 0) {
          console.log('SideMenu - MS user data:', parsed)
          setUserData(parsed)
        } else {
          setUserData(null)
        }
      } catch {
        setUserData(null)
      }
    } else {
      setUserData(null)
    }
  }, [user]) // Odśwież gdy zmieni się user atom

  // Użyj danych z localStorage lub atomu
  const displayEmail = user || ''
  const displayName = userData?.name || userData?.first_name || userData?.given_name || ''

  const userName = useMemo(() => {
    // Jeśli mamy displayName z Microsoft, użyj go
    if (displayName && typeof displayName === 'string') {
      return displayName.split(' ')[0]
    }
    // W przeciwnym razie użyj emaila
    if (!displayEmail || typeof displayEmail !== 'string') return 'User'

    // Bezpiecznie podziel email
    const atIndex = displayEmail.indexOf('@')
    if (atIndex === -1) return displayEmail // Jeśli to nie email, zwróć jak jest

    const [localPart] = displayEmail.split('@')
    const [name] = localPart.split('.')
    return name.charAt(0).toUpperCase() + name.slice(1)
  }, [displayEmail, displayName])

  return (
    <Drawer
      variant='permanent'
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: '#F5F6FA'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          gap: 1
        }}
      >
        <NetworkState />
        <OfflineStatus />
        <Box sx={{ px: 1.5 }}>
          <SelectContent />
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          height: '360px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Calendar />
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <MenuContent />
      </Box>
      <Stack
        direction='row'
        sx={{
          p: 2,
          gap: 1,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Avatar
          sizes='small'
          alt={userName.toUpperCase()}
          src='/static/images/avatar/7.jpg'
          sx={{ width: 36, height: 36 }}
        />
        <Box sx={{ mr: 'auto' }}>
          <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: '16px' }}>
            {userName}
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {displayEmail}
          </Typography>
        </Box>
        <IconButton onClick={clearUserData} aria-label='Logout'>
          <LogoutRoundedIcon />
        </IconButton>
      </Stack>
    </Drawer>
  )
}
